import React, { createContext, FC, useEffect, useRef, useState } from 'react';
import { message } from 'antd';

import {
  GetRecoveryByUsernameResponseDto,
  GetRecoveryMailResponseDto,
  ValidateRecoveryDto,
} from '@services/_api';
import {
  getRecoveryEmail,
  streamRecoveryByUsername,
  validateRecovery,
} from '@services/cstool/recovery-account';

import { IAPInfo } from './iap-modal';

const useRecoveryAccountContextValue = () => {
  const [environment, setEnvironment] = useState<string>();
  const [userId, setUserId] = useState<string>();
  const [recoveryPlayerInfo, setRecoveryPlayerInfo] = useState<{
    Id: string;
    Name: string;
    Clan_Id?: string;
    ClanName?: string;
  } | null>(null);
  const [recoveryEmail, setRecoveryEmail] = useState<GetRecoveryMailResponseDto[]>([]);
  const [recoveryEmailIAPInfo, setRecoveryEmailIAPInfo] = useState<IAPInfo[]>([]);

  const [username, setUsername] = useState<string>();
  const [recoveryByUsername, setRecoveryByUsername] = useState<GetRecoveryByUsernameResponseDto[]>(
    [],
  );
  const [isFetchingByUserId, setIsFetchingByUserId] = useState<boolean>(false);
  const [isFetchingByUsername, setIsFetchingByUsername] = useState<boolean>(false);
  const streamAbortRef = useRef<AbortController | null>(null);

  const handleGetRecoveryEmail = async () => {
    if (!userId || !environment) return;
    setIsFetchingByUserId(true);
    const response = await getRecoveryEmail({ userId, environment }).finally(() => {
      setIsFetchingByUserId(false);
    });
    if (response.ok) {
      const data = response.data as any;
      setRecoveryPlayerInfo(data?.playerInfo ?? null);
      setRecoveryEmail(data?.accounts ?? []);
      setRecoveryEmailIAPInfo(data?.IAPInfo ?? []);
    } else {
      message.error(response.error?.message);
    }
  };

  const handleValidateRecovery = async (props: ValidateRecoveryDto) => {
    const response = await validateRecovery(props);
    return response.data;
  };

  useEffect(() => {
    setRecoveryPlayerInfo(null);
    setRecoveryEmail([]);
    setRecoveryEmailIAPInfo([]);
  }, [userId, environment]);

  const handleGetRecoveryByUsername = async () => {
    if (!username || !environment) return;

    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
    }
    const controller = new AbortController();
    streamAbortRef.current = controller;

    setRecoveryByUsername([]);
    setIsFetchingByUsername(true);

    try {
      await streamRecoveryByUsername(
        { username, environment },
        (player) => setRecoveryByUsername((prev) => [...prev, player]),
        controller.signal,
      );
    } catch (err) {
      if (err?.name !== 'AbortError') {
        message.error(err?.message ?? 'Failed to fetch players');
      }
    } finally {
      setIsFetchingByUsername(false);
    }
  };

  return {
    environment,
    setEnvironment,
    userId,
    setUserId,
    recoveryPlayerInfo,
    recoveryEmail,
    recoveryEmailIAPInfo,
    isFetchingByUserId,
    handleGetRecoveryEmail,
    username,
    setUsername,
    recoveryByUsername,
    handleGetRecoveryByUsername,
    handleValidateRecovery,
    isFetchingByUsername,
  };
};

export type RecoveryAccountContext = ReturnType<typeof useRecoveryAccountContextValue>;

const recoveryAccountContext = createContext<RecoveryAccountContext | null>(null);

export const RecoveryAccountProvider: FC = ({ children }) => {
  const value = useRecoveryAccountContextValue();
  return (
    <recoveryAccountContext.Provider value={value}>{children}</recoveryAccountContext.Provider>
  );
};

export const useRecoveryAccountContext = () => {
  const context = React.useContext(recoveryAccountContext);
  if (!context) {
    throw new Error('useRecoveryAccountContext must be used within a RecoveryAccountProvider');
  }
  return context;
};

export default RecoveryAccountProvider;
